(() => {
  let lastCartTrigger = null;

  const parseSection = (html, sectionId) => {
    const documentFragment = new DOMParser().parseFromString(html, 'text/html');
    return documentFragment.querySelector(`[data-section-id="${sectionId}"]`);
  };

  const replaceHeaderSection = (sectionId, html, openDrawer = true) => {
    const currentHeader = document.querySelector(`[data-section-id="${sectionId}"]`);
    const nextHeader = parseSection(html, sectionId);
    if (!currentHeader || !nextHeader) throw new Error('Unable to refresh the cart drawer.');
    const currentDrawer = document.querySelector(`[data-cart-drawer][data-section-id="${sectionId}"]`);
    const nextDrawer = nextHeader.parentElement?.querySelector(`[data-cart-drawer][data-section-id="${sectionId}"]`);
    currentHeader.replaceWith(nextHeader);
    if (currentDrawer && nextDrawer) currentDrawer.replaceWith(nextDrawer);
    init(document);
    if (openDrawer) document.querySelector(`[data-cart-drawer][data-section-id="${sectionId}"]`)?.open();
  };

  const requestCartSection = async (url, options, sectionId) => {
    const response = await fetch(url, options);
    const result = await response.json();
    if (!response.ok || result.status) throw new Error(result.description || result.message || 'The cart could not be updated.');
    const sectionHtml = result.sections?.[sectionId];
    if (!sectionHtml) throw new Error('The cart response did not include the header section.');
    replaceHeaderSection(sectionId, sectionHtml, true);
    return result;
  };

  class CartDrawer extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready) return;
      this.dataset.ready = 'true';
      this.querySelectorAll('[data-cart-drawer-close]').forEach((button) => button.addEventListener('click', () => this.close()));
      this.querySelectorAll('[data-cart-quantity-action]').forEach((button) => button.addEventListener('click', () => {
        const item = button.closest('[data-cart-item]');
        const input = item?.querySelector('[data-cart-quantity-input]');
        if (!item || !input) return;
        const increment = Number(input.step || 1);
        const quantity = Math.max(0, Number(input.value || 0) + (button.dataset.cartQuantityAction === 'increase' ? increment : -increment));
        this.change(item, quantity);
      }));
      this.querySelectorAll('[data-cart-quantity-input]').forEach((input) => input.addEventListener('change', () => this.change(input.closest('[data-cart-item]'), Math.max(0, Number(input.value || 0)))));
      this.querySelectorAll('[data-cart-remove]').forEach((button) => button.addEventListener('click', () => this.change(button.closest('[data-cart-item]'), 0)));
    }

    open(trigger = null) {
      lastCartTrigger = trigger || lastCartTrigger || document.querySelector('[data-cart-drawer-open]');
      this.setAttribute('aria-hidden', 'false');
      document.body.classList.add('drawer-open');
      document.querySelectorAll('[data-cart-drawer-open]').forEach((button) => button.setAttribute('aria-expanded', 'true'));
      requestAnimationFrame(() => this.querySelector('[data-cart-drawer-close]')?.focus());
    }

    close() {
      this.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('drawer-open');
      document.querySelectorAll('[data-cart-drawer-open]').forEach((button) => button.setAttribute('aria-expanded', 'false'));
      lastCartTrigger?.focus();
    }

    showError(message) {
      const error = this.querySelector('[data-cart-error]');
      if (!error) return;
      error.textContent = message;
      error.hidden = false;
    }

    async change(item, quantity) {
      if (!item || item.classList.contains('is-loading')) return;
      item.classList.add('is-loading');
      try {
        const body = JSON.stringify({ id: item.dataset.lineKey, quantity, sections: this.dataset.sectionId, sections_url: location.pathname });
        await requestCartSection(this.dataset.cartChangeUrl, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body }, this.dataset.sectionId);
      } catch (error) {
        item.classList.remove('is-loading');
        this.showError(error.message);
      }
    }
  }

  if (!customElements.get('cart-drawer')) customElements.define('cart-drawer', CartDrawer);

  const init = (root = document) => {
    root.querySelectorAll('[data-drawer-open]').forEach((button) => {
      if (button.dataset.ready) return;
      button.dataset.ready = 'true';
      button.addEventListener('click', () => {
        const drawer = document.querySelector(`[data-drawer="${button.dataset.drawerOpen}"]`);
        if (!drawer) return;
        drawer.hidden = false;
        document.body.classList.add('drawer-open');
        button.setAttribute('aria-expanded', 'true');
        drawer.querySelector('[data-drawer-close]')?.focus();
      });
    });
    root.querySelectorAll('[data-drawer]').forEach((drawer) => {
      if (drawer.dataset.ready) return;
      drawer.dataset.ready = 'true';
      const close = () => {
        drawer.hidden = true;
        document.body.classList.remove('drawer-open');
        document.querySelector(`[data-drawer-open="${drawer.dataset.drawer}"]`)?.setAttribute('aria-expanded', 'false');
      };
      drawer.addEventListener('click', (event) => { if (event.target === drawer || event.target.closest('[data-drawer-close]')) close(); });
    });
    root.querySelectorAll('[data-cart-drawer-open]').forEach((trigger) => {
      if (trigger.dataset.cartReady) return;
      trigger.dataset.cartReady = 'true';
      trigger.addEventListener('click', (event) => {
        const drawer = document.querySelector('[data-cart-drawer]');
        if (!drawer) return;
        event.preventDefault();
        drawer.open(trigger);
      });
    });
    root.querySelectorAll('form[action*="/cart/add"]').forEach((form) => {
      if (form.dataset.cartAjaxReady || !document.querySelector('[data-cart-drawer]')) return;
      form.dataset.cartAjaxReady = 'true';
      form.addEventListener('submit', async (event) => {
        if (event.defaultPrevented) return;
        event.preventDefault();
        const drawer = document.querySelector('[data-cart-drawer]');
        const submit = form.querySelector('[type="submit"]');
        if (!drawer) return form.submit();
        submit?.setAttribute('disabled', 'disabled');
        try {
          const data = new FormData(form);
          data.append('sections', drawer.dataset.sectionId);
          data.append('sections_url', location.pathname);
          await requestCartSection(drawer.dataset.cartAddUrl, { method: 'POST', headers: { Accept: 'application/json' }, body: data }, drawer.dataset.sectionId);
        } catch (error) {
          drawer.showError(error.message);
        } finally {
          submit?.removeAttribute('disabled');
        }
      });
    });
    root.querySelectorAll('[data-gallery]').forEach((gallery) => {
      if (gallery.dataset.ready) return;
      gallery.dataset.ready = 'true';
      gallery.querySelectorAll('[data-gallery-key]').forEach((thumb) => thumb.addEventListener('click', () => {
        gallery.querySelectorAll('[data-gallery-item]').forEach((media) => media.hidden = media.dataset.galleryItem !== thumb.dataset.galleryKey);
        gallery.querySelectorAll('[data-gallery-key]').forEach((item) => item.classList.toggle('active', item === thumb));
      }));
    });
    root.querySelectorAll('[data-quantity]').forEach((quantity) => {
      if (quantity.dataset.ready) return;
      quantity.dataset.ready = 'true';
      const input = quantity.querySelector('input');
      quantity.querySelectorAll('button').forEach((button) => button.addEventListener('click', () => {
        const delta = button.dataset.quantityAction === 'increase' ? 1 : -1;
        input.value = Math.max(Number(input.min || 1), Number(input.value || 1) + delta);
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }));
    });
    root.querySelectorAll('[data-rest-comfort]').forEach((comfort) => {
      if (comfort.dataset.ready) return;
      comfort.dataset.ready = 'true';
      const select = (index) => {
        comfort.querySelectorAll('[data-rest-comfort-tab]').forEach((tab) => {
          const selected = tab.dataset.restComfortTab === index;
          tab.classList.toggle('active', selected);
          if (tab.getAttribute('role') === 'tab') tab.setAttribute('aria-selected', String(selected));
        });
        comfort.querySelectorAll('[data-rest-comfort-panel]').forEach((panel) => panel.hidden = panel.dataset.restComfortPanel !== index);
      };
      comfort.querySelectorAll('[data-rest-comfort-tab]').forEach((tab) => tab.addEventListener('click', () => select(tab.dataset.restComfortTab)));
      select('0');
    });
    root.querySelectorAll('[data-product-section]').forEach((section) => {
      if (section.dataset.ready) return;
      section.dataset.ready = 'true';
      const select = section.querySelector('[data-variant-select]');
      if (!select) return;
      const variants = JSON.parse(section.querySelector('[data-product-variants]').textContent);
      select.addEventListener('change', () => {
        const variant = variants.find((item) => String(item.id) === select.value);
        if (!variant) return;
        section.querySelector('[data-product-price]').textContent = Shopify.formatMoney(variant.price, section.dataset.moneyFormat);
        const submit = section.querySelector('[data-add-to-cart]');
        submit.disabled = !variant.available;
        submit.querySelector('span').textContent = variant.available ? submit.dataset.availableLabel : submit.dataset.soldOutLabel;
        if (variant.featured_media) section.querySelector(`[data-gallery-key="shopify-${variant.featured_media.id}"]`)?.click();
      });
    });
  };
  window.Shopify = window.Shopify || {};
  Shopify.formatMoney = Shopify.formatMoney || ((cents, format) => (format || '${{amount}}').replace('{{amount}}', (cents / 100).toFixed(2)));
  document.addEventListener('DOMContentLoaded', () => init());
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    document.querySelectorAll('[data-drawer]:not([hidden]) [data-drawer-close]').forEach((button) => button.click());
    document.querySelectorAll('[data-cart-drawer][aria-hidden="false"]').forEach((drawer) => drawer.close());
  });
})();
